// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

// Utils
import globalLogger from '../../../logger/logger';

// Actions
import { localCacheForceClear } from '../../../offline/cache/localCacheActions';

// Components
import DefaultErrorRenderer from './DefaultErrorRenderer';

const getDefaultErrorMessage = (error, info) => `Error rendering component: ${error.toString()}`;

const getDefaultRollbarErrorDetails = (error, info) => ({
    stack: info.componentStack,
});

/**
 * Builds an explicit Rollbar grouping key for render errors when a boundary provides a namespace.
 */
export const getRollbarFingerprint = (loggingFingerprintPrefix, error) => {
    if (!loggingFingerprintPrefix) return undefined;

    return `error-boundary:${loggingFingerprintPrefix}:${error?.name || 'null'}`;
};

/**
 * Preserves boundary-specific details under Rollbar's custom payload when applying a fingerprint override.
 */
export const getRollbarErrorPayload = (errorDetails, fingerprint) => {
    if (!fingerprint) return errorDetails;

    return {
        fingerprint,
        custom: errorDetails,
    };
};

const mapDispatchToProps = (dispatch) => ({
    dispatchLocalCacheForceClear: () => dispatch(localCacheForceClear()),
});

const logger = globalLogger.createChannel('error-boundary');
class ErrorBoundary extends React.Component {
    state = {
        hasRenderError: false,
        error: null,
    };

    static getDerivedStateFromError(error) {
        return {
            hasRenderError: true,
            error,
        };
    }

    handleBeforeUnload = (event) => {
        if (this.state.hasRenderError) {
            const { dispatchLocalCacheForceClear } = this.props;

            dispatchLocalCacheForceClear();
        }
    };

    componentDidMount() {
        window.addEventListener('beforeunload', this.handleBeforeUnload);
    }

    componentWillUnmount() {
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
    }

    componentDidCatch(error, info) {
        const {
            doNotReport,
            getErrorMessage = getDefaultErrorMessage,
            getRollbarErrorDetails = getDefaultRollbarErrorDetails,
            dispatchLocalCacheForceClear,
            loggingFingerprintPrefix,
        } = this.props;

        if (doNotReport) return;

        dispatchLocalCacheForceClear();

        const errorMessage = getErrorMessage(error, info, this.props);
        const errorDetails = getRollbarErrorDetails(error, info, this.props);
        const fingerprint = getRollbarFingerprint(loggingFingerprintPrefix, error);
        const rollbarErrorPayload = getRollbarErrorPayload(errorDetails, fingerprint);

        // Sensitive data audit: SAFE — error auto-sanitized
        logger.error(errorMessage, error, rollbarErrorPayload);
    }

    render() {
        const { ErrorRenderer = DefaultErrorRenderer } = this.props;

        return this.state.hasRenderError ? (
            <ErrorRenderer {...this.props} error={this.state.error} />
        ) : (
            this.props.children
        );
    }
}

ErrorBoundary.propTypes = {
    elementId: PropTypes.string,
    doNotReport: PropTypes.bool,
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
    ErrorRenderer: PropTypes.func,
    getErrorMessage: PropTypes.func,
    getRollbarErrorDetails: PropTypes.func,
    loggingFingerprintPrefix: PropTypes.string,
    dispatchLocalCacheForceClear: PropTypes.func,
};

export default connect(null, mapDispatchToProps)(ErrorBoundary);
